import { formatMonitoringDay, toIsoDate } from '@/app/pages/monitoring/molecules/create-monitoring-brief/sections/monitoring-window/monitoring-window.helpers';

export const RESULTS_PAGE_SIZE = 50;

export interface PublishedPostLink {
  socialPostId?: string | null;
  socialUrl?: string | null;
  postType?: string | null;
}

export interface RawResult {
  postId?: string;
  platform?: string;
  handle?: string;
  views?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  save_count?: number;
  engagements?: number;
  engagementRate?: number;
  followers?: number;
  createTime?: string | Date;
  updatedDate?: string | Date;
  caption?: string;
  tiktokVideoFile?: { cover?: string | null } | null;
  instagramVideoFile?: { cover?: string | null } | null;
  creator?: {
    id?: string;
    firstName?: string | null;
    lastName?: string | null;
    tikTokFollowers?: number | null;
    instaFollowers?: number | null;
  } | null;
}

export interface ResultsStat {
  socialPostId: string;
  creatorId: string;
  creatorName: string;
  handle: string;
  followers: number;
  postedAt: string;
  postedLabel: string;
  postedIso: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  engagement: number;
  engagementRate: number;
  caption: string;
  postUrl: string;
  coverUrl: string | null;
  platform: string;
  /** Reel, Story, Post, TikTok… from the published-content row. Empty until the download task has set it. */
  postType: string;
  /** When Super Social last refreshed this post's stats, as an ISO timestamp. Empty when unknown. */
  statsUpdatedAt: string;
}

export interface BriefCreator {
  id: string;
  name: string;
}

export type ResultsSort = 'posted' | 'followers' | 'views' | 'likes' | 'comments' | 'shares' | 'saves' | 'engagement' | 'rate';
export type ResultsDirection = 'asc' | 'desc';

export interface ResultsViewQuery {
  search: string;
  creatorId: string;
  sort: ResultsSort;
  direction: ResultsDirection;
  page: number;
}

export interface ResultsCreatorOption {
  id: string;
  name: string;
  disabled: boolean;
}

export interface ResultsTotals {
  posts: number;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  averageEngagementRate: number | null;
}

export interface ResultsScan {
  status: 'empty' | 'no-match' | 'list';
  rows: ResultsStat[];
  matched: ResultsStat[];
  page: number;
  pageCount: number;
  totals: ResultsTotals;
  creators: ResultsCreatorOption[];
}

const SORTS = new Set<ResultsSort>(['posted', 'followers', 'views', 'likes', 'comments', 'shares', 'saves', 'engagement', 'rate']);

export function joinResults(
  published: readonly PublishedPostLink[],
  statsByPostId: Record<string, RawResult> | null | undefined,
): ResultsStat[] {
  const links = new Map<string, { url: string; postType: string }>();
  for (const post of published) {
    const id = post.socialPostId?.trim() ?? '';
    if (!id) {
      continue;
    }
    links.set(id, { url: post.socialUrl?.trim() ?? '', postType: post.postType?.trim() ?? '' });
  }

  const stats = statsByPostId ?? {};
  const rows: ResultsStat[] = [];
  for (const key of Object.keys(stats)) {
    const id = key.trim();
    const raw = stats[key];
    if (!id || !raw) {
      continue;
    }
    const link = links.get(id);
    rows.push(toStat(id, raw, link?.url ?? '', link?.postType ?? ''));
  }
  return rows;
}

export function unfilteredViews(posts: readonly ResultsStat[]): number {
  return posts.reduce((total, post) => (post.socialPostId.trim() ? total + post.views : total), 0);
}

export function scanResults(
  posts: readonly ResultsStat[],
  creators: readonly BriefCreator[],
  query: ResultsViewQuery,
): ResultsScan {
  const stats = posts.filter((post) => post.socialPostId.trim().length > 0);
  const creatorIds = new Set(stats.map((post) => post.creatorId));
  const options = creators.map((creator) => ({
    id: creator.id,
    name: creator.name,
    disabled: !creatorIds.has(creator.id),
  }));

  if (stats.length === 0) {
    return {
      status: 'empty',
      rows: [],
      matched: [],
      page: 1,
      pageCount: 0,
      totals: emptyTotals(),
      creators: options,
    };
  }

  const needle = query.search.trim().toLowerCase();
  const matched = stats
    .filter((post) => {
      if (query.creatorId && post.creatorId !== query.creatorId) {
        return false;
      }
      if (!needle) {
        return true;
      }
      return [post.creatorName, post.handle, post.caption].some((value) => value.toLowerCase().includes(needle));
    })
    .sort((left, right) => comparePosts(left, right, query.sort, query.direction));

  if (matched.length === 0) {
    return {
      status: 'no-match',
      rows: [],
      matched: [],
      page: 1,
      pageCount: 0,
      totals: emptyTotals(),
      creators: options,
    };
  }

  const pageCount = Math.ceil(matched.length / RESULTS_PAGE_SIZE);
  const page = Math.min(Math.max(query.page, 1), pageCount);
  const start = (page - 1) * RESULTS_PAGE_SIZE;

  return {
    status: 'list',
    rows: matched.slice(start, start + RESULTS_PAGE_SIZE),
    matched,
    page,
    pageCount,
    totals: totalsFor(matched),
    creators: options,
  };
}

export interface OpenResult {
  post: ResultsStat;
  index: number;
  total: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

/** The opened post is one row of the filtered, sorted list. Previous and next stay inside that list. */
export function openResult(matched: readonly ResultsStat[], socialPostId: string): OpenResult | null {
  const index = matched.findIndex((post) => post.socialPostId === socialPostId);
  if (index < 0) {
    return null;
  }
  return {
    post: matched[index],
    index,
    total: matched.length,
    hasPrevious: index > 0,
    hasNext: index < matched.length - 1,
  };
}

export function stepResult(matched: readonly ResultsStat[], socialPostId: string, step: -1 | 1): OpenResult | null {
  const current = openResult(matched, socialPostId);
  if (!current) {
    return null;
  }
  const next = matched[current.index + step];
  if (!next) {
    return current;
  }
  return openResult(matched, next.socialPostId);
}

export function parseResultsQuery(params: { get(name: string): string | null }): ResultsViewQuery {
  const sort = params.get('sort');
  const direction = params.get('direction');
  const page = Number(params.get('page'));
  return {
    search: params.get('search') ?? '',
    creatorId: params.get('creator') ?? '',
    sort: sort && SORTS.has(sort as ResultsSort) ? (sort as ResultsSort) : 'views',
    direction: direction === 'asc' || direction === 'desc' ? direction : 'desc',
    page: Number.isInteger(page) && page > 0 ? page : 1,
  };
}

export function resultsQueryParams(query: ResultsViewQuery): Record<string, string | null> {
  const defaults = { sort: 'views' as const, direction: 'desc' as const };
  return {
    search: query.search.trim() ? query.search : null,
    creator: query.creatorId || null,
    sort: query.sort === defaults.sort ? null : query.sort,
    direction: query.sort === defaults.sort && query.direction === defaults.direction ? null : query.direction,
    page: query.page > 1 ? String(query.page) : null,
  };
}

export function resultsCsv(rows: readonly ResultsStat[], briefTitle: string): { fileName: string; csv: string; count: number } {
  const header = 'creator,handle,current followers,platform,posted date,views,likes,comments,shares,saves,engagement,engagement rate,caption,post URL';
  const lines = rows.map((row) =>
    [
      row.creatorName,
      row.handle,
      row.followers,
      row.platform,
      row.postedIso,
      row.views,
      row.likes,
      row.comments,
      row.shares,
      row.saves,
      row.engagement,
      row.engagementRate,
      row.caption,
      row.postUrl,
    ].map(csvCell).join(','),
  );
  return {
    fileName: `${fileBase(briefTitle)}-results.csv`,
    csv: [header, ...lines].join('\n'),
    count: rows.length,
  };
}

function toStat(id: string, raw: RawResult, postUrl: string, postType: string): ResultsStat {
  const posted = postedParts(raw.createTime);
  const platform = raw.platform?.trim() || '';
  return {
    socialPostId: id,
    creatorId: raw.creator?.id?.trim() || '',
    creatorName: creatorName(raw),
    handle: raw.handle?.trim() || '',
    followers: currentFollowers(platform, raw),
    postedAt: posted.iso,
    postedLabel: posted.label,
    postedIso: posted.iso,
    views: num(raw.views),
    likes: num(raw.likes),
    comments: num(raw.comments),
    shares: num(raw.shares),
    saves: num(raw.save_count),
    engagement: num(raw.engagements),
    engagementRate: num(raw.engagementRate),
    caption: raw.caption?.trim() ? raw.caption : '',
    postUrl,
    coverUrl: coverUrl(raw),
    platform,
    postType,
    statsUpdatedAt: parsePosted(raw.updatedDate)?.toISOString() ?? '',
  };
}

function currentFollowers(platform: string, raw: RawResult): number {
  const creator = raw.creator;
  const key = platform.toLowerCase();
  if (key === 'tiktok' && typeof creator?.tikTokFollowers === 'number') {
    return creator.tikTokFollowers;
  }
  if (key === 'instagram' && typeof creator?.instaFollowers === 'number') {
    return creator.instaFollowers;
  }
  return num(raw.followers);
}

function creatorName(raw: RawResult): string {
  return [raw.creator?.firstName, raw.creator?.lastName].filter((part) => !!part && part.trim()).join(' ');
}

function coverUrl(raw: RawResult): string | null {
  const tiktok = raw.tiktokVideoFile?.cover?.trim() ?? '';
  if (tiktok) {
    return tiktok;
  }
  const instagram = raw.instagramVideoFile?.cover?.trim() ?? '';
  return instagram || null;
}

function postedParts(value: string | Date | undefined): { label: string; iso: string } {
  const date = parsePosted(value);
  if (!date) {
    return { label: '', iso: '' };
  }
  return { label: formatMonitoringDay(date), iso: toIsoDate(date) };
}

function parsePosted(value: string | Date | undefined): Date | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function comparePosts(left: ResultsStat, right: ResultsStat, sort: ResultsSort, direction: ResultsDirection): number {
  const sign = direction === 'asc' ? 1 : -1;
  const delta = sortValue(left, sort) - sortValue(right, sort);
  if (delta === 0) {
    return left.socialPostId.localeCompare(right.socialPostId);
  }
  return delta * sign;
}

function sortValue(post: ResultsStat, sort: ResultsSort): number {
  switch (sort) {
    case 'posted':
      return parsePosted(post.postedAt)?.getTime() ?? 0;
    case 'followers':
      return post.followers;
    case 'views':
      return post.views;
    case 'likes':
      return post.likes;
    case 'comments':
      return post.comments;
    case 'shares':
      return post.shares;
    case 'saves':
      return post.saves;
    case 'engagement':
      return post.engagement;
    case 'rate':
      return post.engagementRate;
  }
}

function totalsFor(rows: readonly ResultsStat[]): ResultsTotals {
  const totals = emptyTotals();
  for (const row of rows) {
    totals.posts += 1;
    totals.views += row.views;
    totals.likes += row.likes;
    totals.comments += row.comments;
    totals.shares += row.shares;
    totals.saves += row.saves;
    totals.averageEngagementRate = (totals.averageEngagementRate ?? 0) + row.engagementRate;
  }
  if (totals.posts > 0 && totals.averageEngagementRate !== null) {
    totals.averageEngagementRate = totals.averageEngagementRate / totals.posts;
  }
  return totals;
}

function emptyTotals(): ResultsTotals {
  return { posts: 0, views: 0, likes: 0, comments: 0, shares: 0, saves: 0, averageEngagementRate: null };
}

function csvCell(value: string | number): string {
  const text = String(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function fileBase(briefTitle: string): string {
  const base = briefTitle.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return base || 'monitoring';
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}
